import React from "react";
import { withTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { logClientEvent } from "../../services/logService";
import { color, radius, shadow } from "../../styles/theme";

/**
 * Catches render-time crashes anywhere below it, logs them to the backend
 * ActionLog (via logClientEvent — same table ISafeLogger.LogSafe writes to
 * server-side), and shows a recoverable fallback instead of a blank white
 * screen. Class component because React error boundaries require it — no
 * hook equivalent exists.
 */
class ErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    logClientEvent("ClientRenderError", error?.message || "Unknown render error", {
      metadata: { stack: error?.stack, componentStack: info?.componentStack },
    });
  }

  render() {
    if (this.state.hasError) {
      const { t } = this.props;
      return (
        <div style={s.wrap}>
          <div style={s.card}>
            <span style={s.iconBadge}>
              <AlertTriangle size={22} strokeWidth={2} />
            </span>
            <h2 style={s.title}>{t("errorBoundary.title")}</h2>
            <p style={s.message}>{t("errorBoundary.message")}</p>
            <button type="button" className="btn btn-primary" style={s.reloadBtn} onClick={() => window.location.reload()}>
              {t("errorBoundary.reload")}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const s = {
  wrap: {
    minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
    background: color.bg, padding: 24,
  },
  card: {
    width: "100%", maxWidth: 420, background: color.surface, borderRadius: radius.lg,
    boxShadow: shadow.lg, border: `1px solid ${color.border}`, padding: "32px 28px",
    display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 10,
  },
  iconBadge: {
    width: 48, height: 48, borderRadius: "50%", background: color.danger50, color: color.danger500,
    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 4,
  },
  title: { margin: 0, fontSize: 17, fontWeight: 700, color: color.textPrimary },
  message: { margin: "0 0 8px", fontSize: 13.5, color: color.textSecondary, lineHeight: 1.55 },
  reloadBtn: { minWidth: 140 },
};

export default withTranslation("common")(ErrorBoundary);
